import { setForceShowErrors } from '@nsr/react-form/dist/components/redux/slices/formSlice';
import { createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import findIndex from 'lodash/findIndex';

import cloneDeep from 'lodash/cloneDeep';
import { matchPath } from 'react-router-dom';
import { executor } from '@nsr/react-query-builder-executor';
import { api } from '../../../ApiContext';
import { CONSTANTS, applicantRoute, routes } from '../../../Helpers/Configuration';
import { ContentActionOptions } from '../../../Admin/conditionalLogicConstants';
import { setApplicationPageCompletedStatus } from '../slices/applicationSlice';
import { setSettingsValue } from '../slices/settingsSlice';
import { startLoading, stopLoading } from '../../../../redux/shared/slices/isLoadingSlice';
import { saveQueue } from './saveQueue';

const isNotDataEntryPage = (pathName) => {
    const match = matchPath(pathName, {
        path: [
            routes.adminTemplatePreviewReviewPage,
            routes.adminTemplatePreviewSubmitPage,
            routes.adminTemplatePreviewSupplementalItemsPage,
            routes.adminTemplatePreviewSupportingDocumentsPage,
            routes.applicationFormReviewPage,
            routes.applicationFormSubmitPage,
            routes.applicationFormSupplementalItemsPage,
            routes.applicationFormSupportingDocumentsPage,
            routes.applicationFormPayments,
            routes.externalApplicationSubmitPage,
            routes.externalApplicationSupplementalItemsPage,
            routes.externalApplicationSupportingDocumentsPage,
            routes.externalApplicationwReviewPage,
            routes.applicationFormWithdrawnPage,
            routes.externalApplicationPayments,
            routes.importsTemplatePreviewReviewPage,
            routes.importsTemplatePreviewSubmitPage,
            routes.importsTemplatePreviewSupplementalItemsPage,
            routes.importsTemplatePreviewSupportingDocumentsPage,
        ],
    });
    return match !== null;
};
const isApplicationDataEntryPage = (pathName) => {
    return matchPath(pathName, {
        path: [
            routes.adminTemplatePreviewPageById,
            routes.applicationFormPageById,
            routes.externalApplicationPageById,
            routes.importsTemplatePreviewPageById,
            routes.importsApplicationEditPageById
        ]
    });
};
const pageHasErrors = (page, template, errors) => {
    if (!template.pages || !errors || page === undefined || !template.pages[page]) {
        return false;
    }
    const pageId = template.pages[page].id;
    const hasErrors = errors[pageId] && Object.keys(errors[pageId]).length > 0;
    return hasErrors;
};

const isConditionHidden = async (conditions, values) => {
    if (!conditions || conditions.length === 0) return false;
    for (const condition of conditions) {
        const evaluateCondition = condition.fn || executor(condition.query);
        const result = await evaluateCondition(values);
        if ((condition.effect === ContentActionOptions.hide && result) ||
            (condition.effect === ContentActionOptions.show && !result)) {
            return true;
        }
    }
    return false;
};

const getVisibleQuestionIds = async (pageTemplate, values, hiddenPageIds) => {
    const effectiveValues = cloneDeep(values);
    if (hiddenPageIds) {
        hiddenPageIds.forEach(hiddenId => {
            if (effectiveValues[hiddenId]) {
                Object.keys(effectiveValues[hiddenId]).forEach(q => {
                    effectiveValues[hiddenId][q] = null;
                });
            }
        });
    }
    const visibleIds = new Set();
    for (const section of pageTemplate.sections || []) {
        if (await isConditionHidden(section.conditions, effectiveValues)) continue;
        if (section.showRepetition) {
            visibleIds.add(section.id);
        } else {
            for (const group of section.groups || []) {
                for (const question of group.questions || []) {
                    if (question.hidden === true) {
                        continue;
                    }
                    if (!(await isConditionHidden(question.conditions, effectiveValues))) {
                        visibleIds.add(question.id);
                    }
                }
            }
        }
    }
    return visibleIds;
};

const pageHasVisibleErrors = async (page, template, errors, values, hiddenPageIds) => {
    if (!template.pages || !errors || page === undefined || !template.pages[page]) {
        return false;
    }
    const pageTemplate = template.pages[page];
    const pageId = pageTemplate.id;
    if (!errors[pageId] || Object.keys(errors[pageId]).length === 0) {
        return false;
    }
    const visibleIds = await getVisibleQuestionIds(pageTemplate, values, hiddenPageIds);
    const errorKeys = Object.keys(errors[pageId]);
    const visibleErrorKeys = errorKeys.filter(key => visibleIds.has(key));
    const hasErrors = visibleErrorKeys.length > 0;
    return hasErrors;
};
export const createDisableNavigationSelector = () =>
    createSelector(
        state => !!state.settings.inquiryMode,
        state => !!state.settings.supplementalFormMode,
        state => state.template,
        state => state.form.errors,
        (_, location) => location,
        (isInquiryMode, isSupplementalMode, template, errors, location) => {
            const page = isSupplementalMode ? 0 : getPageIndex(template.pages, location);
            if ((isInquiryMode || isSupplementalMode) && pageHasErrors(page, template, errors)) {
                return true;
            }
            return false;
        }
    );

const setPageRoute = (history, pageId, applicationId, externalMode, previewMode, importAdminMode, isAps) => {
    let route;
    switch (pageId) {
        case CONSTANTS.pageNames.review:
            route = applicantRoute(routes.applicationFormReviewPage, applicationId);
            if (previewMode && !isAps) {
                route = applicantRoute(routes.importsTemplatePreviewReviewPage, applicationId);
            }
            if (previewMode && isAps) {
                route = applicantRoute(routes.importsTemplatePreviewReviewPage, applicationId);
            }
            if (externalMode) {
                route = applicantRoute(routes.externalApplicationwReviewPage, applicationId);
            }
            history.push(route);
            break;
        case CONSTANTS.pageNames.supplementalItems:
            route = applicantRoute(routes.applicationFormSupplementalItemsPage, applicationId);
            if (previewMode && !isAps) {
                route = applicantRoute(routes.importsTemplatePreviewSupplementalItemsPage, applicationId);
            }
            if (previewMode && isAps) {
                route = applicantRoute(routes.importsTemplatePreviewSupplementalItemsPage, applicationId);
            }
            if (externalMode) {
                route = applicantRoute(routes.externalApplicationSupplementalItemsPage, applicationId);
            }
            history.push(route);
            break;
        case CONSTANTS.pageNames.supportingDocuments:
            route = applicantRoute(routes.applicationFormSupportingDocumentsPage, applicationId);
            if (previewMode && !isAps) {
                route = applicantRoute(routes.importsTemplatePreviewSupportingDocumentsPage, applicationId);
            }
            if (previewMode && isAps) {
                route = applicantRoute(routes.importsTemplatePreviewSupportingDocumentsPage, applicationId);
            }
            if (externalMode) {
                route = applicantRoute(routes.externalApplicationSupportingDocumentsPage, applicationId);
            }
            history.push(route);
            break;
        case CONSTANTS.pageNames.submission:
            route = applicantRoute(routes.applicationFormSubmitPage, applicationId);
            if (previewMode && !isAps) {
                route = applicantRoute(routes.importsTemplatePreviewSubmitPage, applicationId);
            }
            if (previewMode && isAps) {
                route = applicantRoute(routes.importsTemplatePreviewSubmitPage, applicationId);
            }
            if (externalMode) {
                route = applicantRoute(routes.externalApplicationSubmitPage, applicationId);
            }
            history.push(route);
            break;
        default:
            route = applicantRoute(routes.applicationFormPageById, applicationId, pageId);
            if (previewMode && !isAps) {
                route = applicantRoute(routes.importsTemplatePreviewPageById, applicationId, pageId);
            }
            if (previewMode && isAps) {
                route = applicantRoute(routes.importsTemplatePreviewPageById, applicationId, pageId);
            }
            if (externalMode) {
                route = applicantRoute(routes.externalApplicationPageById, applicationId, pageId);
            }
            if (importAdminMode) {
                route = applicantRoute(routes.importsApplicationEditPageById, applicationId, pageId);
            }
            history.push(route);
    }
};

const setPageCompleteStatus = async (pageId, complete, application, values, revision, dispatch, skipApiUpdate = false) => {
    const completeStatus = complete ? CONSTANTS.applicationPageStatuses.COMPLETED : CONSTANTS.applicationPageStatuses.NOTCOMPLETED;
    const pageStatuses = {
        ...application.pageStatuses,
        [pageId]: completeStatus,
    };

    dispatch(setApplicationPageCompletedStatus({
        pageId,
        complete: completeStatus,
    }));

    if (!skipApiUpdate) {
        dispatch(startLoading());

        // Ensure queue has latest revision from Redux on first use
        if (saveQueue.getRevision() == null) {
            saveQueue.setRevision(revision);
        }

        saveQueue.enqueue({
            type: 'pageComplete',
            execute: async (latestRevision) => {
                const newApplication = Object.assign({}, application, {
                    values,
                    pageStatuses,
                    revision: latestRevision,
                });
                // Do not send status on page-complete updates — only pageStatuses/values are changing
                delete newApplication.status;
                const result = await api.applicationForms.update(newApplication);
                return result;
            },
            onSuccess: (result) => {
                dispatch(stopLoading());
                dispatch(setSettingsValue({
                    name: 'revision',
                    value: result.revision,
                }));
            },
            onError: () => {
                dispatch(stopLoading());
            },
        });
    } else {
        dispatch(setSettingsValue({
            name: 'revision',
            value: revision,
        }));
    }
};
export const setPageAndCloseDialogAction = createAsyncThunk(
    'navigation/setPageAndCloseDialog',
    async (arg, thunkApi) => {
        const {
            history,
            pageId,
            isAps
        } = arg;
        const { dispatch } = thunkApi;
        const state = thunkApi.getState();
        const isPreviewMode = state.settings.previewMode;
        const isInquiry = state.settings.inquiryMode;
        const isImportAdminMode = state.settings.importAdminMode;
        let page;
        const routeMatch = isApplicationDataEntryPage(history.location.pathname);
        if (routeMatch) {
            const pageIndex = findIndex(state.template.pages, { id: routeMatch.params.pageId });
            page = pageIndex;
        }
        if (!isNotDataEntryPage(history.location.pathname)) {
            if (await pageHasVisibleErrors(page, state.template, state.form.errors, state.form.values, state.settings.hiddenPageIds)) {
                setPageCompleteStatus(
                    state.template.pages[page].id, false, state.application, state.form.values, state.settings.revision, dispatch, (isPreviewMode || isInquiry || isImportAdminMode)
                );

                dispatch(setForceShowErrors({
                    name: state.template.pages[page].id,
                    value: true,
                }));
            }
            else if (page >= 0) {
                setPageCompleteStatus(
                    state.template.pages[page].id, true, state.application, state.form.values, state.settings.revision, dispatch, (isPreviewMode || isInquiry || isImportAdminMode)
                );

                dispatch(setForceShowErrors({
                    name: state.template.pages[page].id,
                    value: false,
                }));
            }
        }
        setPageRoute(history, pageId, state.application.id, state.settings.externalMode, state.settings.previewMode, state.settings.importAdminMode, isAps);
        dispatch(setSettingsValue({
            name: 'showMobileSidebarDialog',
            value: false,
        }));
        return Promise.resolve(true);
    }
);

export const setPagesInitialStatusAction = createAsyncThunk(
    'navigation/setPagesInitialStatusAction',
    async (arg, thunkApi) => {

        const { dispatch } = thunkApi;
        const state = thunkApi.getState();

        Object.entries(state.application.pageStatuses).filter(([, status]) => status == CONSTANTS.applicationPageStatuses.NOTCOMPLETED).map(([pageId]) => pageId).forEach(pageId => {
            dispatch(setForceShowErrors({
                name: pageId,
                value: true,
            }));
        });

        return Promise.resolve(true);
    }
);
const getPageIndex = (pages, pathname) => {
    let page;
    const routeMatch = isApplicationDataEntryPage(pathname);
    if (routeMatch) {
        const pageIndex = findIndex(pages, { id: routeMatch.params.pageId });
        page = pageIndex;
    }
    return page;
};

export const goBackAction = createAsyncThunk(
    'navigation/goBack',
    async (arg, thunkApi) => {
        const {
            history,
            previousPageId,
            isAps
        } = arg;
        const { dispatch } = thunkApi;
        const state = thunkApi.getState();
        const page = getPageIndex(state.template.pages, history.location.pathname);
        if (state.settings.inquiryMode && pageHasErrors(page, state.template, state.form.errors)) {
            return;
        }
        dispatch(setPageAndCloseDialogAction({
            history,
            pageId: previousPageId,
            isAps
        }));
    }
);

export const goForwardAction = createAsyncThunk(
    'navigation/goForward',
    async (arg, thunkApi) => {
        const {
            history,
            nextPageId,
            isAps
        } = arg;
        const { dispatch } = thunkApi;
        const state = thunkApi.getState();
        const pageIndex = getPageIndex(state.template.pages, history.location.pathname);
        if (state.settings.inquiryMode && pageHasErrors(pageIndex, state.template, state.form.errors)) {
            return;
        }
        if (!nextPageId) {
            dispatch(setPageAndCloseDialogAction({
                history,
                pageId: CONSTANTS.pageNames.review,
                isAps
            }));
            return;
        }
        dispatch(setPageAndCloseDialogAction({
            history,
            pageId: nextPageId,
            isAps
        }));
    }
);
